import { uniqBy } from 'lodash';
import { computed, nextTick, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { MDM_TYPES } from '@/constants/mdm';
import { useFlashesStore } from '@/stores';
import {
  CUSTOMERS_STANDARD_TYPE,
  InternalFilterGroup,
  InternalFilterPart,
  OPEN_OPPS_STANDARD_TYPE,
  PROSPECTS_STANDARD_TYPE,
  Population,
  PopulationStats,
  RecommendedFiltersResponse,
  StandardPopulationType,
} from '@/types/populations';
import { Source } from '@/types/sources';

export function usePopulationFilters() {
  const filterGroupList = ref<InternalFilterGroup[]>([]);
  const originalFilterState = ref<InternalFilterGroup[]>([]);
  const canUserAddFilterGroup = ref(false);
  const completeFilterSources = ref<Source[]>([]);
  const recommendedFilters = ref<RecommendedFiltersResponse | null>(null);
  const fallbackSuggestion = (popStandardType: StandardPopulationType | null) =>
    computed(() => {
      if (!popStandardType) return '';
      const suggestionsMap = {
        [CUSTOMERS_STANDARD_TYPE]:
          'Account Type = Customer <span class="font-normal">or</span> Lifecycle Stage = Customer',
        [PROSPECTS_STANDARD_TYPE]: 'Account Type = Prospect',
        [OPEN_OPPS_STANDARD_TYPE]:
          'Opportunity: Closed = false <span class="font-normal">or</span> Is Deal Closed? = false',
      };
      return suggestionsMap[popStandardType];
    });

  const flashesStore = useFlashesStore();

  const isFilterGroupListEmpty = computed(() => {
    return filterGroupList.value.every((filterGroup) => {
      if (!filterGroup.tableName) return true;
      return filterGroup.filter_parts.length === 0;
    });
  });

  const hasFiltersChanged = computed(() => {
    const currentNonEmptyFilters = filterGroupList.value.filter(
      (filterGroup) => {
        if (!filterGroup.tableName) return false;

        const nonEmptyFilterParts = filterGroup.filter_parts.filter(
          (filter) => {
            return filter.source_field_id && filter.operator;
          },
        );

        return nonEmptyFilterParts.length > 0;
      },
    );

    const originalNonEmptyFilters = originalFilterState.value.filter(
      (filterGroup) => {
        if (!filterGroup.tableName) return false;

        const nonEmptyFilterParts = filterGroup.filter_parts.filter(
          (filter) => {
            return filter.source_field_id && filter.operator;
          },
        );

        return nonEmptyFilterParts.length > 0;
      },
    );

    return (
      (currentNonEmptyFilters.length > 0 &&
        originalNonEmptyFilters.length === 0) ||
      (originalNonEmptyFilters.length > 0 &&
        currentNonEmptyFilters.length === 0) ||
      JSON.stringify(currentNonEmptyFilters) !==
        JSON.stringify(originalNonEmptyFilters)
    );
  });

  function handleAddFilterGroup(
    value: boolean,
    selectedFeed: unknown,
    completeFilterSources: Source[],
  ) {
    if (value && selectedFeed && completeFilterSources.length > 0) {
      const newFilterGroup: InternalFilterGroup = {
        filter_expression: [],
        filter_parts: [],
        tableName: '',
        mdmType: '',
      };

      filterGroupList.value = [...filterGroupList.value, newFilterGroup];
    }
  }

  function hasValidFilterParts(filterGroup: InternalFilterGroup): boolean {
    if (!filterGroup.tableName) return false;

    return filterGroup.filter_parts.some((filter) => {
      return filter.source_field_id && filter.operator;
    });
  }

  function clearFilterGroup(groupIndex: number) {
    const filterGroup = filterGroupList.value[groupIndex];
    if (!filterGroup) return;

    filterGroupList.value[groupIndex] = {
      filter_expression: [],
      filter_parts: [],
      tableName: '',
      mdmType: '',
    };

    if (filterGroupList.value.length === 0) {
      const newFilterGroup: InternalFilterGroup = {
        filter_expression: [],
        filter_parts: [],
        tableName: '',
        mdmType: '',
      };
      filterGroupList.value = [newFilterGroup];
    }
  }

  function handleDeleteFilterGroup(filterGroup: InternalFilterGroup) {
    const index = filterGroupList.value.findIndex(
      (group) => JSON.stringify(group) === JSON.stringify(filterGroup),
    );

    if (index !== -1) {
      filterGroupList.value = filterGroupList.value.filter(
        (_, i) => i !== index,
      );

      if (filterGroupList.value.length === 0) {
        const newFilterGroup: InternalFilterGroup = {
          filter_expression: [],
          filter_parts: [],
          tableName: '',
          mdmType: '',
        };
        filterGroupList.value = [newFilterGroup];
      }
    }
  }

  let isUpdatingFilterGroupList = false;

  function handleChangeFilterGroupList(
    newFilterGroupList: InternalFilterGroup[],
  ) {
    if (!isUpdatingFilterGroupList) {
      isUpdatingFilterGroupList = true;
      filterGroupList.value = newFilterGroupList;
      isUpdatingFilterGroupList = false;
    }
  }

  async function fetchFilterData(
    population:
      | (Population & {
          meta?: PopulationStats;
          isEmpty?: boolean;
          tag?: string;
        })
      | null,
    _sources: Source[],
    offlinePartnerUuid?: string,
  ) {
    filterGroupList.value = [];
    originalFilterState.value = [];

    if (!population?.source_id) return;

    const { data, error } = await crossbeamApi.GET(
      '/v0.2/sources/{id}/filterable-sources',
      {
        params: {
          path: { id: population.source_id },
          query: offlinePartnerUuid
            ? { offline_partner_org_uuid: offlinePartnerUuid }
            : {},
        },
      },
    );
    if (error) {
      flashesStore.addUnhandledError(new Error(error));
      return;
    }
    const forbiddenMdmTypes = [MDM_TYPES.DEAL_CONTACT] as string[];
    completeFilterSources.value = uniqBy(data.items as Source[], 'id').filter(
      (source) => !forbiddenMdmTypes.includes(source.mdm_type),
    );
  }

  function initializeFilterGroups(
    population:
      | (Population & {
          meta?: PopulationStats;
          isEmpty?: boolean;
          tag?: string;
        })
      | null,
    completeFilterSources: Source[],
  ) {
    if (population?.source_filter_expressions) {
      const existingFilterGroups: InternalFilterGroup[] = [];

      const tableNames = Object.keys(population.source_filter_expressions);

      tableNames.forEach((tableName) => {
        const filterData = population.source_filter_expressions?.[tableName];
        if (
          filterData &&
          filterData.filter_parts &&
          filterData.filter_expression
        ) {
          const filterPartsWithCombinators: (typeof filterData.filter_parts)[0][] =
            [];
          for (const item of filterData.filter_expression) {
            if (item === 'AND' || item === 'OR') {
              continue;
            }
            const filterPart = filterData.filter_parts.find(
              (part) => part.label === item,
            );
            if (filterPart) {
              filterPartsWithCombinators.push(filterPart);
            }
          }

          existingFilterGroups.push({
            tableName,
            filter_expression: filterData.filter_expression,
            filter_parts: filterPartsWithCombinators,
            mdmType: completeFilterSources.find(
              (source) => source.table === tableName,
            )?.mdm_type,
          });
        }
      });

      filterGroupList.value = existingFilterGroups;
      originalFilterState.value = JSON.parse(
        JSON.stringify(existingFilterGroups),
      );
    }
  }

  const hasInvalidFilters = computed(() => {
    if (filterGroupList.value.length === 0) return false;

    return filterGroupList.value.some((filterGroup) => {
      if (!filterGroup.filter_parts || filterGroup.filter_parts.length === 0) {
        return !!filterGroup.tableName;
      }

      const hasCombinators = filterGroup.filter_expression.some(
        (item: unknown) => item === 'AND' || item === 'OR',
      );

      if (hasCombinators) {
        const completeFilterParts = filterGroup.filter_parts.filter(
          (filter) => {
            const hasField = filter.source_field_id;
            const hasOperator = filter.operator;
            const needsOperand =
              hasOperator &&
              !['is empty', 'is not empty'].includes(filter.operator || '');
            const hasOperand = needsOperand
              ? filter.operand !== null &&
                filter.operand !== undefined &&
                (Array.isArray(filter.operand)
                  ? filter.operand.length > 0
                  : true)
              : true;

            return hasField && hasOperator && (!needsOperand || hasOperand);
          },
        );

        if (completeFilterParts.length < 2) return true;
      }

      return filterGroup.filter_parts.some((filter) => {
        const hasField = filter.source_field_id;
        const hasOperator = filter.operator;
        const hasOperand =
          filter.operand !== null &&
          filter.operand !== undefined &&
          (Array.isArray(filter.operand) ? filter.operand.length > 0 : true);
        const hasDate =
          filter.operand !== null &&
          filter.operand !== undefined &&
          typeof filter.operand === 'string' &&
          filter.operand.includes('-');

        const hasAnySelections =
          hasField || hasOperator || hasOperand || hasDate;

        if (!hasAnySelections) return true;

        const needsOperand =
          hasOperator &&
          !['is empty', 'is not empty'].includes(filter.operator || '');

        const isComplete =
          hasField && hasOperator && (!needsOperand || hasOperand);

        return hasAnySelections && !isComplete;
      });
    });
  });

  function addRecommendedFilter(filterInfo: {
    field: { source_id: number; id: number };
    operator: { id: string };
    operands: unknown;
    label: string;
    combinator?: string;
  }) {
    const source = completeFilterSources.value.find(
      (s) => s.id === filterInfo.field.source_id,
    );

    if (!source) {
      return;
    }

    const tableName = source.table;
    const mdmType = source.mdm_type;

    const field = source.fields?.find((f) => f.id === filterInfo.field.id);

    let processedOperand = filterInfo.operands;
    if (field?.data_type === 'boolean' && Array.isArray(filterInfo.operands)) {
      processedOperand = filterInfo.operands.map((operand) =>
        typeof operand === 'boolean' ? operand.toString() : operand,
      );
    }

    const newFilterPart = {
      label: filterInfo.label,
      operand: processedOperand,
      operator: filterInfo.operator.id,
      source_field_id: filterInfo.field.id,
    };

    const existingFilterGroup = filterGroupList.value.find(
      (group) => group.tableName === tableName,
    );

    if (!existingFilterGroup) {
      const emptyFilterGroupIndex = filterGroupList.value.findIndex(
        (group) => !group.tableName || group.tableName === '',
      );

      if (emptyFilterGroupIndex !== -1) {
        filterGroupList.value[emptyFilterGroupIndex] = {
          filter_expression: [filterInfo.label],
          filter_parts: [newFilterPart],
          tableName,
          mdmType,
        };
      } else {
        const newFilterGroup: InternalFilterGroup = {
          filter_expression: [filterInfo.label],
          filter_parts: [newFilterPart],
          tableName,
          mdmType,
        };
        filterGroupList.value.push(newFilterGroup);
      }
    } else {
      existingFilterGroup.filter_parts.push(newFilterPart);

      const combinator = filterInfo.combinator || 'AND';
      existingFilterGroup.filter_expression.push(combinator);
      existingFilterGroup.filter_expression.push(filterInfo.label);
    }
  }

  async function fetchAndSetRecommendedFilters(
    sourceId: number,
    standardType: StandardPopulationType,
  ) {
    const { data, error } = await crossbeamApi.GET(
      '/v0.1/populations-suggest-filter',
      {
        params: {
          query: {
            source_id: sourceId,
            standard_pop_type: standardType,
          },
        },
      },
    );
    if (error) {
      flashesStore.addUnhandledError(new Error(error));
      return;
    }

    recommendedFilters.value = data.recommended_filters;
    if (
      recommendedFilters.value &&
      typeof recommendedFilters.value === 'object'
    ) {
      isApplyingRecommendedFilters.value = true;
      hasRecommendedFiltersApplied.value = true;
      justAppliedRecommendedFilters.value = true;
      const tableNames = Object.keys(recommendedFilters.value);

      if (tableNames.length > 0) {
        tableNames.forEach((tableName) => {
          const tableFilters = recommendedFilters.value?.[tableName];

          if (
            tableFilters &&
            tableFilters.filter_parts &&
            Array.isArray(tableFilters.filter_parts) &&
            tableFilters.filter_parts.length > 0
          ) {
            tableFilters.filter_parts.forEach(
              (filterPart: InternalFilterPart, index: number) => {
                if (
                  filterPart &&
                  'source_field_id' in filterPart &&
                  'operator' in filterPart &&
                  'operand' in filterPart &&
                  'label' in filterPart
                ) {
                  const source = completeFilterSources.value.find((s) =>
                    s.fields?.some((f) => f.id === filterPart.source_field_id),
                  );

                  if (
                    source &&
                    source.id &&
                    filterPart.source_field_id &&
                    filterPart.operator &&
                    filterPart.label
                  ) {
                    const filterInfo = {
                      field: {
                        source_id: source.id,
                        id: filterPart.source_field_id,
                      },
                      operator: { id: filterPart.operator },
                      operands: filterPart.operand,
                      label: filterPart.label,
                      combinator: index === 0 ? undefined : 'AND',
                    };

                    addRecommendedFilter(filterInfo);
                  }
                }
              },
            );
          }
        });
      }
    }

    nextTick(() => {
      isApplyingRecommendedFilters.value = false;
      originalFilterState.value = JSON.parse(
        JSON.stringify(filterGroupList.value),
      );
      setTimeout(() => {
        justAppliedRecommendedFilters.value = false;
      }, 200);
    });
  }

  const hasRecommendedFiltersApplied = ref(false);
  const isApplyingRecommendedFilters = ref(false);
  const justAppliedRecommendedFilters = ref(false);

  watch(
    filterGroupList,
    () => {
      if (
        hasRecommendedFiltersApplied.value &&
        !isApplyingRecommendedFilters.value &&
        !justAppliedRecommendedFilters.value
      ) {
        hasRecommendedFiltersApplied.value = false;
      }
    },
    { deep: true },
  );

  return {
    filterGroupList,
    originalFilterState,
    canUserAddFilterGroup,
    completeFilterSources,
    isFilterGroupListEmpty,
    hasFiltersChanged,
    handleAddFilterGroup,
    handleDeleteFilterGroup,
    handleChangeFilterGroupList,
    fetchFilterData,
    initializeFilterGroups,
    hasValidFilterParts,
    clearFilterGroup,
    hasInvalidFilters,
    addRecommendedFilter,
    recommendedFilters,
    fallbackSuggestion,
    hasRecommendedFiltersApplied,
    fetchAndSetRecommendedFilters,
  };
}
